import {
  seriesMeta,
} from "../core/config.js";
import {
  axisRangeForData,
  combinedValueRange,
  displayValueExtent,
  formatAxisLabel,
  singleSeriesAxisRange,
  valueRange,
  yLabelsForRange,
} from "./scales.js";
import { lerp } from "../core/math.js";

export function auxScaleConfig(data, keys, options: Record<string, any> = {}) {
  const fit = options.fit ?? false;
  const ranges = keys.map((key) => displayValueExtent(data, key, seriesMeta[key])).filter(Boolean);
  if (keys.length === 1) {
    const key = keys[0];
    const meta = seriesMeta[key];
    // Fit mode tightens a lone aux trace to the visible window; valueRange keeps
    // Bus Volts on its fixed electrical band, matching the primary lane's behavior.
    const range = fit ? valueRange(data, key, meta, true) : singleSeriesAxisRange(data, key, meta);
    return {
      label: meta.axisLabel ?? meta.label,
      labels: yLabelsForRange(range),
      range,
      independent: false,
    };
  }

  const groups = new Set(keys.map((key) => seriesMeta[key]?.auxGroup ?? key));
  if (groups.size === 1) {
    const group = groups.values().next().value;
    const range = group === "turboTemp"
      ? sharedTurboTempRangeForKeys(data, keys)
      : combinedValueRange(data, keys);
    if (group === "fuel") range.min = Math.max(0, range.min);
    if (rangesShareScale(ranges, group)) {
      return {
        label: seriesMeta[keys[0]].axisLabel ?? "Aux channels",
        labels: yLabelsForRange(range),
        range,
        independent: false,
      };
    }
  }

  if (rangesShareMixedNumericScale(ranges, groups)) {
    const range = combinedValueRange(data, keys);
    range.min = Math.max(0, range.min);
    return {
      label: "Aux values (mixed units)",
      labels: yLabelsForRange(range),
      range,
      independent: false,
    };
  }

  return {
    label: "Aux channels (independent scales)",
    labels: ["Aux high", "", "", "", "Aux low"],
    range: null,
    independent: true,
  };
}

export function sharedMetaRangeForKeys(keys) {
  const metas = keys.map((key) => seriesMeta[key]).filter(Boolean);
  const min = metas.map((meta) => meta.min).filter(Number.isFinite);
  const max = metas.map((meta) => meta.max).filter(Number.isFinite);
  return {
    min: min.length ? Math.min(...min) : 0,
    max: max.length ? Math.max(...max) : 1,
  };
}

export function sharedTurboTempRangeForKeys(data, keys) {
  const baseRange = sharedMetaRangeForKeys(keys);
  return axisRangeForData(data, keys, baseRange, {
    lowerIncrement: 50,
    lowerHeadroom: 25,
    upperIncrement: 25,
    upperHeadroom: 25,
  });
}

export function rangesShareMixedNumericScale(ranges, groups) {
  if (ranges.length < 2) return false;
  const groupNames = [...groups];
  if (!groupNames.every((group) => ["fuel", "electrical"].includes(group))) return false;
  const combinedMin = ranges.reduce((min, range) => Math.min(min, range.min), Infinity);
  const combinedMax = ranges.reduce((max, range) => Math.max(max, range.max), -Infinity);
  return combinedMax - combinedMin <= 30;
}

export function rangesShareScale(ranges, group) {
  if (ranges.length < 2) return true;
  if (group === "percentPowerEstimate" || group === "horsepowerEstimate") return true;
  const combinedMin = ranges.reduce((min, range) => Math.min(min, range.min), Infinity);
  const combinedMax = ranges.reduce((max, range) => Math.max(max, range.max), -Infinity);
  const combinedSpan = Math.max(combinedMax - combinedMin, 1);
  const spans = ranges.map((range) => Math.max(range.max - range.min, 1));
  const largestSpan = spans.reduce((max, span) => Math.max(max, span), 1);
  const smallestSpan = spans.reduce((min, span) => Math.min(min, span), Infinity);
  let minOverlapRatio = 1;
  ranges.forEach((range, index) => {
    ranges.slice(index + 1).forEach((other) => {
      const overlap = Math.max(0, Math.min(range.max, other.max) - Math.max(range.min, other.min));
      minOverlapRatio = Math.min(minOverlapRatio, overlap / Math.min(Math.max(range.max - range.min, 1), Math.max(other.max - other.min, 1)));
    });
  });
  const spanRatio = largestSpan / smallestSpan;
  const maxCombinedSpan = group === "temp" ? 140 : group === "fuel" ? 30 : largestSpan * 2.4;
  if (group === "temp") return combinedSpan <= maxCombinedSpan;
  return spanRatio <= 4 && combinedSpan <= Math.max(largestSpan * 2.8, maxCombinedSpan) && minOverlapRatio > 0.08;
}

export function yLabelsForSingleSeries(key) {
  const meta = seriesMeta[key];
  const labels = [];
  for (let i = 0; i <= 4; i += 1) {
    const value = lerp(meta.max, meta.min, i / 4);
    labels.push(formatAxisLabel(value));
  }
  return labels;
}
